import { siteIdentity } from '../content/siteContent';
import { wpPages } from '../content/wpPages.generated';
import type { PageBlock } from '../lib/types';

const productionOrigin = siteIdentity.siteOrigin;

function firstParagraph(blocks: PageBlock[]) {
  return blocks.find((block): block is Extract<PageBlock, { type: 'paragraph' }> => block.type === 'paragraph')?.text ?? '';
}

function curatedMetadata(slug: 'about' | 'gallery' | 'contact') {
  const page = wpPages[slug];
  return { title: `${page.title} | ${siteIdentity.name}`, description: firstParagraph(page.blocks) };
}

const metadata: Record<string, { title: string; description: string }> = {
  '/': { title: siteIdentity.name, description: siteIdentity.tagline },
  '/about': curatedMetadata('about'),
  '/gallery': curatedMetadata('gallery'),
  '/contact': curatedMetadata('contact'),
  [siteIdentity.catalogPath]: { title: `${siteIdentity.catalogLabel} | ${siteIdentity.name}`, description: `Browse ${siteIdentity.name}'s ${siteIdentity.catalogLabel.toLowerCase()}.` },
  '/blog': { title: `Blog | ${siteIdentity.name}`, description: `Stories and updates from ${siteIdentity.name}.` }
};

export function metadataForPath(pathname: string) {
  const path = pathname === '/' ? '/' : pathname.replace(/\/$/, '');
  const selected = metadata[path] ?? { title: siteIdentity.name, description: siteIdentity.tagline };
  return { ...selected, canonicalUrl: `${productionOrigin}${path === '/' ? '/' : path}` };
}

function meta(name: string, content: string, property = false) {
  const attribute = property ? 'property' : 'name';
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${name}"]`);
  if (!element) { element = document.createElement('meta'); element.setAttribute(attribute, name); document.head.appendChild(element); }
  element.content = content;
}

function writeMetadata(selected: { title: string; description: string; canonicalUrl: string }) {
  document.title = selected.title;
  meta('description', selected.description);
  meta('og:title', selected.title, true); meta('og:description', selected.description, true); meta('og:url', selected.canonicalUrl, true);
  meta('twitter:card', 'summary_large_image'); meta('twitter:title', selected.title); meta('twitter:description', selected.description);
  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
  canonical.href = selected.canonicalUrl;
}

export function applyMetadata(pathname: string) {
  writeMetadata(metadataForPath(pathname));
}

/** Overrides the fixed metadata map with a loaded item's own title/description, once it's fetched (e.g. a blog post or catalog item). */
export function applyItemMetadata(pathname: string, title: string, description: string) {
  writeMetadata({ title: `${title} | ${siteIdentity.name}`, description, canonicalUrl: `${productionOrigin}${pathname}` });
}
